import React, { useState } from "react";
import { Form, Button, Card, Alert, Spinner, Badge, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import Head from "../../components/layout/head/Head";
import useAuth from "../../hooks/auth/useAuth";
import apiConfig from "../../services/apiConfig";
import "./ConsultaRucPage.css";

const ConsultaRucPage = () => {
    const { user } = useAuth();
    const [ruc, setRuc] = useState("");
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validar que el RUC no esté vacío
        if (!ruc || ruc.trim() === "") {
            setError("El RUC es obligatorio");
            return;
        }

        // Validar formato básico de RUC (números y guión opcional)
        const rucLimpio = ruc.replace(/[^0-9-]/g, "");
        if (rucLimpio.length < 6) {
            setError("El RUC debe tener al menos 6 dígitos");
            return;
        }

        setError("");
        setLoading(true);
        setResultado(null);

        console.log("Consultando RUC:", rucLimpio);
        console.log("Token:", user.token ? "Presente" : "Ausente");

        try {
            const response = await fetch(`${apiConfig.baseUrl}/api/de/consultaRUC`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user.token}`,
                },
                body: JSON.stringify({ RUC: rucLimpio }),
            });

            console.log("Response status:", response.status);
            console.log("Response OK:", response.ok);

            if (!response.ok) {
                // Si la respuesta no es OK, intentar leer el error
                const errorText = await response.text();
                console.log("Error response:", errorText);
                let errorMessage = "Error al consultar el RUC";
                
                try {
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.message || errorMessage;
                } catch {
                    // Si no es JSON, usar el texto tal cual
                    errorMessage = errorText || errorMessage;
                }
                
                throw new Error(errorMessage);
            }

            const data = await response.json();
            console.log("Data recibida:", data);

            setResultado(data);

            // Mostrar mensaje según el resultado
            if (data.codigoRespuesta === "0502") {
                toast.success("RUC encontrado exitosamente");
            } else if (data.codigoRespuesta === "0500") {
                toast.info("RUC no existe en el sistema SIFEN");
            } else if (data.codigoRespuesta === "Error") {
                toast.error(data.mensajeRespuesta);
            } else {
                toast.warning(`Código ${data.codigoRespuesta}: ${data.mensajeRespuesta}`);
            }

        } catch (err) {
            console.error("Error al consultar RUC:", err);
            toast.error("Error al realizar la consulta");
            setError(err.message || "Error al realizar la consulta");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setRuc("");
        setResultado(null);
        setError("");
    };

    const formatRUC = (value) => {
        // Permitir solo números y guión
        const cleaned = value.replace(/[^0-9-]/g, "");
        setRuc(cleaned);
    };

    return (
        <>
            <Head title="Consulta RUC - SIFEN" />
            <div className="consulta-ruc-container">
                <div className="consulta-ruc-header">
                    <h2>Consulta de RUC</h2>
                    <p className="text-muted">
                        Consultar información de contribuyentes en el sistema SIFEN
                    </p>
                </div>

                <Row>
                    <Col lg={6} className="mx-auto">
                        <Card className="shadow-sm">
                            <Card.Body>
                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>RUC a consultar</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Ej: 80012345-9 o 80012345"
                                            value={ruc}
                                            onChange={(e) => formatRUC(e.target.value)}
                                            maxLength={15}
                                            disabled={loading}
                                            isInvalid={!!error}
                                            autoFocus
                                        />
                                        <Form.Text className="text-muted">
                                            Ingrese el RUC sin puntos. El guión y dígito verificador son opcionales.
                                        </Form.Text>
                                        <Form.Control.Feedback type="invalid">
                                            {error}
                                        </Form.Control.Feedback>
                                    </Form.Group>

                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={loading}
                                            className="flex-grow-1"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        className="me-2"
                                                    />
                                                    Consultando...
                                                </>
                                            ) : (
                                                "Consultar"
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={handleReset}
                                            disabled={loading}
                                        >
                                            Limpiar
                                        </Button>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>

                        {/* Resultado de la consulta */}
                        {resultado && (
                            <Card className="mt-4 shadow-sm">
                                <Card.Header className="bg-light">
                                    <h5 className="mb-0">Resultado de la consulta</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-3">
                                        <strong>Estado de la consulta:</strong>
                                        <div className="mt-2">
                                            <Badge 
                                                bg={
                                                    resultado.codigoRespuesta === "0502" ? "success" :
                                                    resultado.codigoRespuesta === "0500" ? "warning" : "danger"
                                                }
                                                className="me-2"
                                            >
                                                {resultado.codigoRespuesta}
                                            </Badge>
                                            <span>{resultado.mensajeRespuesta}</span>
                                        </div>
                                    </div>

                                    {resultado.contribuyente && (
                                        <>
                                            <hr />
                                            <h6 className="text-primary mb-3">Datos del Contribuyente</h6>
                                            
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>RUC:</strong></Col>
                                                <Col sm={8}>{resultado.contribuyente.ruc || "N/A"}</Col>
                                            </Row>
                                            
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Razón Social:</strong></Col>
                                                <Col sm={8}>{resultado.contribuyente.razonSocial || "N/A"}</Col>
                                            </Row>
                                            
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Estado:</strong></Col>
                                                <Col sm={8}>
                                                    <Badge 
                                                        bg={resultado.contribuyente.codigoEstado === "1" ? "success" : "secondary"}
                                                        className="me-2"
                                                    >
                                                        {resultado.contribuyente.codigoEstado}
                                                    </Badge>
                                                    {resultado.contribuyente.descripcionEstado || "N/A"}
                                                </Col>
                                            </Row>
                                            
                                            <Row className="mb-2">
                                                <Col sm={4}><strong>Factura Electrónica:</strong></Col>
                                                <Col sm={8}>
                                                    {resultado.contribuyente.facturaElectronica === "S" ? (
                                                        <Badge bg="success">Sí</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">No</Badge>
                                                    )}
                                                </Col>
                                            </Row>
                                        </>
                                    )}

                                    {!resultado.contribuyente && resultado.codigoRespuesta === "0500" && (
                                        <Alert variant="info" className="mb-0 mt-3">
                                            <i className="bi bi-info-circle me-2"></i>
                                            El RUC consultado no existe en la base de datos de la SET.
                                        </Alert>
                                    )}

                                    {resultado.codigoRespuesta === "Error" && (
                                        <Alert variant="danger" className="mb-0 mt-3">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {resultado.mensajeRespuesta}
                                        </Alert>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Información adicional */}
                        <Alert variant="info" className="mt-4">
                            <Alert.Heading className="h6">
                                <i className="bi bi-info-circle me-2"></i>
                                Información
                            </Alert.Heading>
                            <ul className="mb-0 small">
                                <li>La consulta se realiza contra el sistema SIFEN de la SET (Subsecretaría de Estado de Tributación).</li>
                                <li>El sistema reintenta automáticamente hasta 3 veces en caso de errores transitorios.</li>
                                <li>Código <strong>0502</strong>: RUC encontrado y válido.</li>
                                <li>Código <strong>0500</strong>: RUC no existe en el sistema.</li>
                                <li>Los datos mostrados son los registrados oficialmente ante la SET.</li>
                            </ul>
                        </Alert>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default ConsultaRucPage;
